/**
 * How to read `OperatingHours` — the one set of rules for what a day in a
 * location's hours means (ADR-359).
 *
 * WHY IT EXISTS. Five places read location hours, each with its own code, and
 * they drifted: the voice agent once treated a missing day as "unknown" while
 * the Locations page showed it as "Closed". Readers that use this module agree
 * by construction. The voice agent and the Locations page use it today; slot
 * filtering, the scheduling context and the campaign call window are expected
 * to move onto it, each in its own change.
 *
 * THE RULE FOR A DAY:
 *   - missing or `null` → closed. Every reader already agreed on this; the
 *     Locations page saves a day it was told is closed as `null`.
 *   - a malformed time, or a close that is not after the open → invalid. Not
 *     closed: nobody said it was, and a reader decides what to do with it.
 *
 * Pure: the clock is a parameter, and the only dependency is `Intl`.
 */

import type { OperatingHours } from '../schemas/operating-hours';

/** The day keys of `OperatingHours`, Monday first. */
export const WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type WeekDay = (typeof WEEK)[number];

/** What one day of a location's hours says. Times are minutes since local midnight. */
export type DayReading =
  { kind: 'open'; open: number; close: number } | { kind: 'closed' } | { kind: 'invalid' };

/** `HH:MM` (24-hour) to minutes since midnight; null if malformed. */
export function parseClockTime(hhmm: string): number | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** One day of `hours`, read by the rule above. */
export function readDay(hours: OperatingHours, day: WeekDay): DayReading {
  const value = hours[day];
  if (value === null || value === undefined) return { kind: 'closed' };
  const open = parseClockTime(value.open);
  const close = parseClockTime(value.close);
  if (open === null || close === null || close <= open) return { kind: 'invalid' };
  return { kind: 'open', open, close };
}

/**
 * Whether any day opens. Hours that open on no day are hours that were never
 * set — an empty object, or a form saved with every day switched off — not an
 * office that never opens.
 */
export function hasOpeningHours(hours: OperatingHours | null | undefined): boolean {
  return !!hours && WEEK.some((day) => readDay(hours, day).kind === 'open');
}

const ZONE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/** `Intl` weekday names to day keys. */
const BY_NAME: Record<string, WeekDay> = {
  Monday: 'monday',
  Tuesday: 'tuesday',
  Wednesday: 'wednesday',
  Thursday: 'thursday',
  Friday: 'friday',
  Saturday: 'saturday',
  Sunday: 'sunday',
};

/**
 * The location-local day and minutes since midnight at `now`. Null for an
 * unknown zone, which `Intl` throws on, or an invalid date.
 */
export function localDayAndMinutes(
  now: Date,
  timezone: string
): { day: WeekDay; minutes: number } | null {
  if (Number.isNaN(now.getTime())) return null;
  let formatter = ZONE_FORMATTERS.get(timezone);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });
    } catch {
      return null;
    }
    ZONE_FORMATTERS.set(timezone, formatter);
  }
  const parts: Record<string, string> = {};
  for (const p of formatter.formatToParts(now)) parts[p.type] = p.value;
  const day = parts.weekday ? BY_NAME[parts.weekday] : undefined;
  if (!day) return null;
  return { day, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}

/** Whether a location is open at an instant, by its regular hours. */
export type OpenStatus =
  { open: true; closesAt: number } | { open: false; nextOpen: { day: WeekDay; at: number } };

/**
 * Open or closed at `now` in the location's own zone, and until or from when.
 * Looks a week ahead, so a location open one day a week still finds that day.
 *
 * Null when it cannot say: no opening hours, an unknown zone, or an `invalid`
 * day it would have to read past to answer.
 */
export function openStatusAt(
  hours: OperatingHours | null | undefined,
  timezone: string | null | undefined,
  now: Date
): OpenStatus | null {
  if (!hours || !timezone || !hasOpeningHours(hours)) return null;
  const local = localDayAndMinutes(now, timezone);
  if (!local) return null;

  const todayIndex = WEEK.indexOf(local.day);
  const today = readDay(hours, local.day);
  if (today.kind === 'invalid') return null;
  if (today.kind === 'open') {
    if (local.minutes >= today.open && local.minutes < today.close) {
      return { open: true, closesAt: today.close };
    }
    if (local.minutes < today.open) {
      return { open: false, nextOpen: { day: local.day, at: today.open } };
    }
  }
  for (let ahead = 1; ahead <= 7; ahead++) {
    const day = WEEK[(todayIndex + ahead) % 7]!;
    const reading = readDay(hours, day);
    if (reading.kind === 'invalid') return null;
    if (reading.kind === 'open') return { open: false, nextOpen: { day, at: reading.open } };
  }
  return null;
}
